// packages/api/src/client.ts
// D-1 / D-6 — ONE shared API-client core for Nexus + Portal.
//
// Consolidates the two near-identical HTTP clients that previously lived in
// apps/nexus/src/repositories/BaseRepository.ts and
// apps/portal/src/services/apiClient.ts:
//   - fail-fast base URL resolution (F13: configured wins, dev fallback, prod throw)
//   - auth headers (Bearer from the shared token key) + credentials: 'include'
//   - typed get/post/patch/delete/postFormData with one error envelope
//     (ApiError carries statusCode + code + warnings; 401 and non-JSON handling
//     are configurable so each app keeps the error surface its tests pin)
//   - the presigned-upload flow as ONE implementation (request URL → PUT to B2
//     → confirm; the backend only persists document metadata in the confirm
//     step, so the 3-step flow is canonical — see workers routes/storage.ts +
//     batches.ts confirm_document / companies.ts confirm_kkf_extract).

/** Shared localStorage token key (both apps already used this exact key). */
export const TOKEN_KEY = 'chemtrace_auth_token'

/**
 * Unified API error envelope. `code` carries backend machine codes (e.g. 422
 * REFERENCE_UNKNOWN); `warnings` carries non-blocking policy warnings.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: Response,
    /** Machine-readable error code from the API envelope (e.g. 422 codes). */
    public code?: string,
    /** Non-blocking warning array some endpoints return (e.g. policy warnings). */
    public warnings?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Fail-fast API base resolution (F13 / F13c / F13d — P-4):
 * a configured VITE_API_URL always wins; a missing one is only tolerated in
 * dev (legacy UAT fallback keeps local dev working); a production build
 * without it is a misconfiguration and throws instead of silently targeting
 * the wrong API. `missingMessage` lets each app keep its pinned error text.
 */
export function resolveApiBaseUrl(
  env: Record<string, unknown> = import.meta.env,
  missingMessage = 'VITE_API_URL is not set — refusing to target the UAT API. Configure it at build time.',
): string {
  const configured = env.VITE_API_URL
  if (typeof configured === 'string' && configured.length > 0) return configured
  if (env.DEV) return 'https://api.nma.uat.sr'
  throw new Error(missingMessage)
}

/** Build auth headers: Bearer token from localStorage + merged extras. */
export function getAuthHeaders(
  extra: Record<string, string> = {},
  tokenKey: string = TOKEN_KEY,
): Record<string, string> {
  const headers = { ...extra }
  const token = localStorage.getItem(tokenKey)
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

/** Response of POST /api/v1/presigned_upload (backend storage.ts). */
export interface PresignedUpload {
  uploadUrl: string
  publicUrl: string
  key: string
  expiresAt?: string
}

/** Canonical document metadata sent to confirm endpoints (matches the backend confirmDocumentSchema). */
export interface UploadDocumentMeta {
  url: string
  filename: string
  contentType: string
  size: number
  uploadedAt: string
}

export interface ApiClientOptions {
  /** API origin, e.g. 'https://api.nma.uat.sr'. Defaults to resolveApiBaseUrl(). */
  baseUrl?: string
  /** Prefix prepended to paths that do not already start with '/api/'. Default '/api/v1'. */
  apiPrefix?: string
  /** localStorage key holding the Bearer token. Default TOKEN_KEY. */
  tokenKey?: string
  /**
   * Extract { error, errors, code, warnings } from non-2xx JSON bodies.
   * Portal behavior (default true). Nexus pinned 'Request failed: <statusText>'
   * messages → BaseRepository passes false.
   */
  extractErrorEnvelope?: boolean
  /**
   * A 2xx response whose body is not JSON is a contract violation: throw
   * loudly instead of resolving to undefined. Nexus behavior (true). Portal
   * pins the silent undefined fallback → default false.
   */
  loudNonJson?: boolean
}

/**
 * The shared HTTP client. Paths may be given with a full '/api/v1/...' prefix
 * (nexus style) or relative '/resource' (portal style) — the prefix is added
 * only when missing, so both apps call the same implementation.
 */
export class ApiClient {
  private readonly baseUrl: string
  private readonly apiPrefix: string
  private readonly tokenKey: string
  private readonly extractErrorEnvelope: boolean
  private readonly loudNonJson: boolean

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? resolveApiBaseUrl()
    this.apiPrefix = options.apiPrefix ?? '/api/v1'
    this.tokenKey = options.tokenKey ?? TOKEN_KEY
    this.extractErrorEnvelope = options.extractErrorEnvelope ?? true
    this.loudNonJson = options.loudNonJson ?? false
  }

  async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    const url = new URL(this.buildUrl(path))
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value != null) url.searchParams.append(key, value)
    })

    const response = await fetch(url.toString(), {
      headers: getAuthHeaders({}, this.tokenKey),
      credentials: 'include', // Also send session cookie as fallback
    })
    return this.handleResponse<T>(response)
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(this.buildUrl(path), {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }, this.tokenKey),
      credentials: 'include',
      body: JSON.stringify(body),
    })
    return this.handleResponse<T>(response)
  }

  async patch<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(this.buildUrl(path), {
      method: 'PATCH',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }, this.tokenKey),
      credentials: 'include',
      body: JSON.stringify(body),
    })
    return this.handleResponse<T>(response)
  }

  async delete<T = void>(path: string, body?: unknown): Promise<T> {
    const options: RequestInit = {
      method: 'DELETE',
      headers: getAuthHeaders({}, this.tokenKey),
      credentials: 'include',
    }
    if (body !== undefined) {
      options.headers = getAuthHeaders({ 'Content-Type': 'application/json' }, this.tokenKey)
      options.body = JSON.stringify(body)
    }
    const response = await fetch(this.buildUrl(path), options)
    return this.handleResponse<T>(response)
  }

  /** Nexus API compat: DELETE with a JSON body (portal's delete(path, body)). */
  async deleteWithBody<T = void>(path: string, body: unknown): Promise<T> {
    return this.delete<T>(path, body)
  }

  async postFormData<T>(path: string, formData: FormData): Promise<T> {
    const response = await fetch(this.buildUrl(path), {
      method: 'POST',
      headers: getAuthHeaders({}, this.tokenKey),
      credentials: 'include',
      body: formData,
    })
    return this.handleResponse<T>(response)
  }

  // ---------------------------------------------------------------------------
  // Presigned-upload flow — ONE implementation (D-6).
  //
  // Steps 1+2 are identical everywhere; step 3 (confirm) is endpoint-specific
  // (confirm_document vs confirm_kkf_extract vs register-staging reference),
  // so it is supplied as a callback over the canonical document metadata.
  // The backend REQUIRES the confirm step to persist metadata into D1
  // (routes/storage.ts records the grant; routes/batches.ts:confirm_document +
  // routes/companies.ts:confirm_kkf_extract store the metadata) — the
  // portal's 3-step uploadDocument flow is therefore the canonical one.
  // ---------------------------------------------------------------------------

  /** Step 1: request a presigned upload URL from the backend. */
  async getPresignedUpload(
    prefix: string,
    file: File,
    fallbackContentType?: string,
  ): Promise<PresignedUpload> {
    const contentType = file.type || fallbackContentType || 'application/octet-stream'
    const response = await fetch(this.buildUrl('/presigned_upload'), {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }, this.tokenKey),
      credentials: 'include',
      body: JSON.stringify({ prefix, filename: file.name, contentType }),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(
        `Failed to get upload URL: ${(err as { error?: string }).error || response.statusText}`,
      )
    }

    return (await response.json()) as PresignedUpload
  }

  /** Step 2: PUT the file bytes directly to the presigned URL. */
  async putToPresigned(uploadUrl: string, file: File, contentType?: string): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' },
      body: file,
    })

    if (!response.ok) {
      throw new Error(`Direct upload failed: ${response.status} ${response.statusText}`)
    }
  }

  /**
   * Canonical presigned flow: request URL → PUT to B2 → `confirm` with the
   * document metadata. Never returns a partial upload — throws on any step.
   */
  async uploadAndConfirm<T = unknown>(
    prefix: string,
    file: File,
    fallbackContentType: string | undefined,
    confirm: (document: UploadDocumentMeta) => Promise<T>,
  ): Promise<T> {
    const presigned = await this.getPresignedUpload(prefix, file, fallbackContentType)
    const contentType = file.type || fallbackContentType || 'application/octet-stream'
    await this.putToPresigned(presigned.uploadUrl, file, contentType)
    return confirm({
      url: presigned.publicUrl,
      filename: file.name,
      contentType,
      size: file.size,
      uploadedAt: new Date().toISOString(),
    })
  }

  /** 3-step batch-document upload: presigned → PUT → POST confirm_document. */
  async uploadDocument<T = unknown>(
    batchId: string,
    documentType: string,
    file: File,
    fallbackContentType = 'application/octet-stream',
  ): Promise<T> {
    return this.uploadAndConfirm(`batches/${batchId}`, file, fallbackContentType, (document) =>
      this.post<T>(`/chemical_product_batches/${batchId}/confirm_document`, {
        documentType,
        document,
      }),
    )
  }

  // ---------------------------------------------------------------------------
  // Error helpers (previously BaseRepository.parseErrorJson / formatErrorMessage).
  // ---------------------------------------------------------------------------

  async parseErrorJson(response: Response | undefined): Promise<any> {
    if (!response) return {}
    try {
      return await response.json()
    } catch {
      return {}
    }
  }

  /** Parse and format API error responses into user-friendly messages. */
  async formatErrorMessage(error: unknown, context: string): Promise<string> {
    if (!(error instanceof ApiError)) throw error

    if (error.statusCode === 401) return 'Unauthorized - Please log in'
    if (error.statusCode === 404) return `${context} not found`

    const data = await this.parseErrorJson(error.response)

    if (Array.isArray(data.errors)) {
      return data.errors.join(', ')
    }

    if (typeof data.errors === 'object' && data.errors !== null) {
      return Object.entries(data.errors)
        .map(([field, msgs]) => {
          const label = this.humanizeField(field)
          const messages = Array.isArray(msgs) ? msgs : [msgs]
          const cleaned = messages.map((msg: string) =>
            msg.replace(/can't be blank$/i, 'is required'),
          )
          return `${label}: ${cleaned.join(', ')}`
        })
        .join('; ')
    }

    return data.error || `Failed to ${context.toLowerCase()}`
  }

  /** Convert snake_case or camelCase field names to readable labels. */
  private humanizeField(field: string): string {
    const known: Record<string, string> = {
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email address',
      password: 'Password',
      passwordConfirmation: 'Password confirmation',
      phone: 'Phone number',
      userType: 'User type',
      companyId: 'Company',
      companyRole: 'Role',
    }
    return (
      known[field] ||
      field
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^\w/, (c) => c.toUpperCase())
        .trim()
    )
  }

  private buildUrl(path: string): string {
    const normalized = path.startsWith('/') ? path : `/${path}`
    if (normalized.startsWith('/api/')) return `${this.baseUrl}${normalized}`
    return `${this.baseUrl}${this.apiPrefix}${normalized}`
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        throw new ApiError('Unauthorized - Please log in', 401, response)
      }
      if (this.extractErrorEnvelope) {
        let message = response.statusText
        try {
          const errorData = await response.json()
          if (errorData.error) message = errorData.error
          else if (errorData.errors) message = JSON.stringify(errorData.errors)
          throw new ApiError(message, response.status, response, errorData.code, errorData.warnings)
        } catch (err) {
          if (err instanceof ApiError) throw err
          // Fallback to statusText
        }
        throw new ApiError(message, response.status, response)
      }
      throw new ApiError(`Request failed: ${response.statusText}`, response.status, response)
    }
    if (response.status === 204) {
      return undefined as T
    }
    try {
      return await response.json()
    } catch {
      if (this.loudNonJson) {
        // A 2xx response whose body is not JSON is a contract violation — fail
        // loudly instead of resolving to `undefined as T` and crashing later.
        throw new ApiError(
          `Invalid response from server (expected JSON): ${response.status} ${response.statusText}`,
          response.status,
          response,
        )
      }
      return undefined as T
    }
  }
}
